/**
 * The post-landing "Signed in as <email>" cue.
 *
 * WHY THIS IS SECURITY MACHINERY AND NOT POLISH. `/auth/link` auto-proceeds for
 * a signed-out visitor with no conflicting state (see AuthLinkLanding's file
 * header for the full policy). That removes the click that used to be the
 * login-CSRF consent step on that one path, so the defence moves from CONSENT to
 * VISIBILITY: the person who opened the link is told, unmissably and in the app
 * they just landed in, which account they are now writing into. An attacker who
 * gets a victim to open a link for the attacker's own address no longer gets a
 * silent takeover; they get a victim who is looking at a toast naming an address
 * that is not theirs. The hard control remains server-side either way -- Logto
 * refuses a token/address pair it did not mint together
 * (`one_time_token.email_mismatch`).
 *
 * Two rules that are easy to get wrong:
 *
 * 1. THE EMAIL COMES FROM THE RESOLVED IDENTITY, never from the marker and never
 *    from the link's `email` param. The param is attacker-authored URL text; a
 *    cue that echoed it would confirm the account the attacker NAMED rather than
 *    the one the visitor is actually in, which is precisely the lie the cue
 *    exists to prevent. The marker below therefore carries its arming time
 *    and nothing else -- there is no field for an address, on purpose.
 *
 * 2. IT IS ONE-SHOT. `/callback` reads it and clears it in the same place that
 *    acts on it, so a Back-navigation cannot replay a confirmation for a
 *    sign-in that already happened.
 */
import { AUTH_FLOW_TTL_MS } from '@utils/authFlowTtl';
import { defineSessionSlot, isTrue, type SessionSlot } from '@utils/sessionSlot';

/**
 * Per-tab, and named like the other `meetball_*` sessionStorage keys. Holds the
 * arming time and nothing else.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: issue #1117. This was a profile-wide
 * localStorage mirror (`mb.magicLink.signedInCue`), so a marker armed in one tab
 * was spent by a sign-in completing in ANOTHER, and a sign-in starting in any
 * tab cleared a marker armed in all of them. The cue never needed to cross tabs:
 * both arming sites (`/auth/link`'s auto redemption and the door's continuation
 * redemption) start the sign-in from the same document, the hop to Logto and
 * back is a full-page navigation in that tab, and `/callback` can only complete
 * in the tab that started it because Logto keeps the sign-in session in
 * sessionStorage too (`@logto/browser` `BrowserStorage`, key `signInSession`).
 * So a per-tab key spans the redemption exactly, the same property
 * `redemptionEmail.ts` and `silentReauthSlot` rely on.
 *
 * Registered in `storageCleanup.ts` twice, like `AUTH_LINK_EMAIL_KEY`: in
 * `SESSION_STORAGE_KEYS` so a sign-out takes it, and in `AUTH_HANDOVER_KEYS` so
 * `/auth/link`'s account-switch sweep, which runs in the same click as the
 * redemption it keeps the cue for, does not delete it.
 */
export const SIGNED_IN_CUE_KEY = 'meetball_signed_in_cue';

/**
 * Armed (`signedInCueSlot.write(true)`) immediately before a redemption NOBODY
 * ON THIS DEVICE CLICKED FOR, and only then. A gate the visitor clicked through
 * already told them whose account they were entering, on a screen built to say
 * so; re-announcing it afterwards would be noise.
 *
 * TWO ARMING CALLERS, not one -- this used to say `/auth/link` "and only then",
 * and the door became the second when continuation landed:
 *
 *   `/auth/link`, immediately before an AUTO-proceeded redemption
 *   (`AuthLinkLanding.tsx`). The click it stands in for is the one the gate
 *   would have asked for.
 *
 *   The DOOR, immediately before redeeming a token another device approved
 *   (`AuthDoor.tsx`, the claim's ok branch). The click it stands in for
 *   happened on the OTHER device, so this document signs in with no
 *   interaction of its own at all -- the sharpest case the cue exists for.
 *
 * `write` RETURNS WHETHER THE MARKER IS REALLY THERE, and BOTH callers are
 * required to act on it, with the same rule and two different shapes of
 * stand-down: `/auth/link` does not auto-proceed on `false`, it renders the gate
 * and asks for the click instead; the door does not redeem on `false`, it puts a
 * Continue button on the screen and waits for the visitor to press it. Rule 3,
 * and the reason this is not fire-and-forget. A third caller that ignores the
 * return value reintroduces the silent takeover on exactly the locked-down
 * browsers the read-back exists to detect: Web Storage is missing in exactly the
 * places this matters (iOS Safari with "Block All Cookies" throws on the
 * property access itself, Safari private mode has historically thrown on
 * setItem, and a partitioned WebView can accept a write and keep nothing), and
 * the token capture is a module singleton, so nothing else in the flow notices.
 * A failed write also leaves NO marker behind, so a gate the visitor clicks
 * through (which has already named the account) never fires a cue with no auto
 * path behind it.
 *
 * READ with `read() === true`. `/callback` reads it where it decides whether to
 * show the cue and clears it in an effect, so rule 2 holds.
 *
 * CLEAR (`signedInCueSlot.clear()`) drops this tab's marker without firing
 * anything. TWO call sites, each at a moment where the marker stopped meaning
 * "a redemption is in flight that nobody clicked for":
 *
 *   `/callback`, on EVERY error type: the redemption did not complete, so there
 *   is nothing to confirm, and a survivor would otherwise attach itself to
 *   whatever sign-in reaches the callback next within the TTL -- announcing an
 *   auto-redemption to somebody who typed their password.
 *
 *   AuthProvider's `logtoSignIn`, the wrapper around the SDK's `signIn` that
 *   every hook starting a Logto sign-in receives (useSignInOut,
 *   useAuthNavigation's guard, useInvalidGrantHandler's recovery attempts). A
 *   sign-in that starts is a sign-in the old marker is not about, so it goes
 *   -- unless the caller passes `keepSignedInCue`, which only the redemptions
 *   it was armed FOR do: the door's continuation redemption, and `/auth/link`'s
 *   `handleConfirm` on every gate except `same_account` and `switch_account`.
 *   Those two named a RESOLVED identity before the click, so the redemption
 *   they start owes no second announcement; the others name the account from
 *   `params.email`, which rule 1 above rules out as a naming source, so the cue
 *   they owe still stands. See `SignInOptions.keepSignedInCue`.
 *
 * (`/callback`'s success path is not a third: it SPENDS the marker after acting
 * on it, rather than dropping it unfired.)
 *
 * AIDEV-NOTE [MEETBALL magic-link]: issues #1116 and #1118. The second site
 * used to be three scattered ones -- the door's `signIn` wrapper and `sendLink`,
 * and `handleConfirm` on the two naming gates -- and the set was not exhaustive
 * over the routes back to `/callback`. `useInvalidGrantHandler`'s direct
 * `logtoSignIn` legs never mount the door, and a build with `magicLinkAuth` off
 * routes nothing to it while `/auth/link` still arms. So a marker left by a
 * handoff that died before its redirect was spent by a sign-in the visitor
 * performed deliberately, including a silent `prompt=none` re-auth that
 * `/callback` otherwise renders invisible. One site on the only function every
 * sign-in passes through cannot miss a route, and needs no keep-site for a
 * send: sending a link starts no sign-in, and the `sendLink` clear destroyed
 * the cue of a stalled redirect that later landed. Do not add a clear site;
 * if a new path starts a sign-in, it does so through `logtoSignIn`.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the failure REVEAL on `/auth/link` used to
 * be a caller and deliberately is not one any more. A timer that fires
 * because a redirect is slow is not evidence that a redemption failed -- prod
 * measured a 1710ms gap between `signIn()` and the new document committing --
 * and clearing there destroyed the cue of sign-ins that were succeeding. Reveal
 * is a UI hedge; destruction belongs where the meaning really changed.
 */
export const signedInCueSlot: SessionSlot<true> = defineSessionSlot({
  key: SIGNED_IN_CUE_KEY,
  ttlMs: AUTH_FLOW_TTL_MS,
  isValue: isTrue,
});

/**
 * Deletes the cue as releases before #1117 stored it: a profile-wide
 * localStorage mirror. Nothing reads that key any more, so this only stops an
 * orphan from sitting in the profile. Run once at boot (`src/index.tsx`).
 *
 * AIDEV-TODO: delete this and its call once no client older than #1117 can
 * still be running, i.e. a release or two after it ships.
 */
export const removeLegacySignedInCueMirror = (): void => {
  try {
    globalThis.localStorage.removeItem('mb.magicLink.signedInCue');
  } catch {
    // Nothing to do: storage that throws holds nothing to remove.
  }
};

/** The cue's copy, in one place so the screen and its test cannot drift. */
export const signedInCueMessage = (email: string | undefined): string =>
  email ? `Signed in as ${email}` : "You're signed in";
